<template>
	<ListUpcomingOffer
		:release="upcomingRelease"
		:title="title"
		:isTitleInWatchlist="isTitleInWatchlist"
		@onNotifyMe="$emit('onNotifyMe')"
	>
		<template #banner>
			<div v-if="upcomingRelease.isFirst" class="upcoming-banner">
				<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path
						d="M0.88892 8.95515C0.886316 11.3431 2.08856 13.5714 4.08603 14.8805C6.0835 16.1896 8.60692 16.4031 10.7951 15.4473C12.9844 14.4916 14.5434 12.4969 14.9409 10.1418C15.3385 7.78666 14.5217 5.38999 12.7691 3.76844L13.7421 2.79968C13.9158 2.62607 13.9158 2.34395 13.7421 2.17035C13.5685 1.99675 13.2873 1.99674 13.1137 2.17035L12.0798 3.20419C10.6632 2.18857 8.92183 1.73197 7.19005 1.92208C5.45741 2.11132 3.85583 2.93424 2.69361 4.23275C1.53041 5.53048 0.888054 7.21204 0.88892 8.95515ZM7.96448 2.77026C9.6051 2.77026 11.1789 3.42217 12.3396 4.58275C13.4993 5.74335 14.1512 7.31719 14.1512 8.95786C14.1512 10.5993 13.4993 12.1723 12.3387 13.333C11.1781 14.4927 9.60427 15.1446 7.96294 15.1437C6.32232 15.1437 4.7485 14.4918 3.58872 13.3312C2.42899 12.1698 1.7771 10.5959 1.77796 8.95546C1.7797 7.3157 2.43248 5.74279 3.59221 4.58301C4.75194 3.42417 6.32488 2.77226 7.96465 2.77052L7.96448 2.77026Z"
						fill="#00E03C"
					/>
					<path d="M5.63989 0H10.291V0.888889H5.63989V0Z" fill="#00E03C" />
					<path
						d="M7.96446 9.39961C8.08251 9.39961 8.19537 9.35274 8.2787 9.26941C8.36203 9.18607 8.40891 9.07323 8.40891 8.95516V4.92383H7.52002V8.95516C7.52002 9.07322 7.5669 9.18607 7.65023 9.26941C7.73356 9.35274 7.84639 9.39961 7.96446 9.39961Z"
						fill="#00E03C"
					/>
				</svg>
				<div>
					{{ upcomingBanner }}
				</div>
			</div>
		</template>
	</ListUpcomingOffer>
</template>

<script setup lang="ts">
import { UpcomingReleaseLabel } from '@/@types/graphql-types';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { formattedReleaseDate } from '@/helpers/date-helper';
import { BuyBoxFilterOptions } from '@/stores/useUserStoreTypes';
import { SnowplowContext } from '@/helpers/tracking/providers';
import ListUpcomingOffer from '@/features/buybox/components/list/ListUpcomingOffer.vue';
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import type { UpcomingReleaseBuybox } from '@/features/buybox/types/types.ts';

const { language, country } = storeToRefs(useLanguageStore());

const { t } = useI18n();

const props = defineProps<{
	upcomingRelease: UpcomingReleaseBuybox;
	title: TitleDetail;
	isTitleInWatchlist?: boolean;
	isPromotedBuyBox?: boolean;
	filter: BuyBoxFilterOptions;
	additionalContexts: SnowplowContext[];
}>();

defineEmits<{
	(e: 'onNotifyMe'): void;
}>();

const isTheatrical = computed(() => props.upcomingRelease.offer.releaseType === 'THEATRICAL');
const upcomingOfferLabel = computed(() => props.upcomingRelease.offer.label);

const releaseDateFormatted = computed(() => {
	const formattedDate = formattedReleaseDate(
		props.upcomingRelease.offer.releaseDate,
		upcomingOfferLabel.value,
		language.value,
		'short',
		country.value,
		true
	).heading;

	if (isSeason.value) {
		const year = new Date(props.upcomingRelease.offer.releaseDate).getFullYear();
		return `${formattedDate} ${year}`;
	}

	return formattedDate;
});

const providerClearName = computed(() => {
	return props.upcomingRelease.offer.package?.packageId === 350
		? 'Apple TV'
		: props.upcomingRelease.offer.package?.clearName;
});
const isSeason = computed(() => {
	return [
		UpcomingReleaseLabel.Spring,
		UpcomingReleaseLabel.Summer,
		UpcomingReleaseLabel.Autumn,
		UpcomingReleaseLabel.Winter,
	].includes(upcomingOfferLabel.value);
});
const isDate = computed(() => upcomingOfferLabel.value === UpcomingReleaseLabel.Date);
const isMonth = computed(() => upcomingOfferLabel.value === UpcomingReleaseLabel.Month);
const isYear = computed(() => upcomingOfferLabel.value === UpcomingReleaseLabel.Year);
const isTBA = computed(() => upcomingOfferLabel.value === UpcomingReleaseLabel.ToBeAnnounced);
const isTomorrow = computed(() => upcomingOfferLabel.value === UpcomingReleaseLabel.Tomorrow);
const isToday = computed(() => upcomingOfferLabel.value === UpcomingReleaseLabel.Today);

const showProviderName = computed(() => {
	if (!providerClearName.value) return false;
	return !props.upcomingRelease.isInGroup || props.upcomingRelease.allSameProviders;
});

const upcomingBanner = computed(() => {
	if (
		isTheatrical.value
	) {
		if (isDate.value) return t('WEBAPP_COMING_TO_THEATERS_ON_DATE', { date: releaseDateFormatted.value });
		if (isMonth.value) return t('WEBAPP_COMING_TO_THEATERS_MONTH', { month: releaseDateFormatted.value });
		if (isSeason.value) return t('WEBAPP_COMING_TO_THEATERS_SEASON', { season: releaseDateFormatted.value });
		if (isYear.value) return t('WEBAPP_COMING_TO_THEATERS_YEAR', { year: releaseDateFormatted.value });
		if (isToday.value) return t('WEBAPP_COMING_TO_THEATRES_TODAY');
		if (isTomorrow.value) return t('WEBAPP_COMING_TO_THEATRES_TOMORROW');
		if (isTBA.value) return t('WEBAPP_COMING_TO_THEATERS_TBA');
	}

	// prettier-ignore
	else if (showProviderName.value) {
		// prettier-ignore
		if (isDate.value) return t('WEBAPP_COMING_TO_PROVIDERS_ON_DATE', { provider: providerClearName.value, date: releaseDateFormatted.value });
		// prettier-ignore
		if (isMonth.value) return t('WEBAPP_COMING_TO_PROVIDER_MONTH', { provider: providerClearName.value, month: releaseDateFormatted.value });
		// prettier-ignore
		if (isSeason.value) return t('WEBAPP_COMING_TO_PROVIDER_SEASON', { provider: providerClearName.value, season: releaseDateFormatted.value });
		// prettier-ignore
		if (isYear.value) return t('WEBAPP_COMING_TO_PROVIDER_YEAR', { provider: providerClearName.value, year: releaseDateFormatted.value });
		if (isToday.value) return t('WEBAPP_COMING_TO_PROVIDER_TODAY', { provider: providerClearName.value });
		if (isTomorrow.value) return t('WEBAPP_COMING_TO_PROVIDER_TOMORROW', { provider: providerClearName.value });
		if (isTBA.value) return t('WEBAPP_COMING_TO_PROVIDER_TBA', { provider: providerClearName.value });
	}

	if (isDate.value) return t('WEBAPP_COMING_TO_STREAMING_ON_DATE', { date: releaseDateFormatted.value });
	if (isMonth.value) return t('WEBAPP_COMING_TO_STREAMING_MONTH', { month: releaseDateFormatted.value });
	if (isSeason.value) return t('WEBAPP_COMING_TO_STREAMING_SEASON', { season: releaseDateFormatted.value });
	if (isYear.value) return t('WEBAPP_COMING_TO_STREAMING_YEAR', { year: releaseDateFormatted.value });
	if (isToday.value) return t('WEBAPP_COMING_TO_STREAMING_TODAY', { day: releaseDateFormatted.value });
	if (isTomorrow.value) return t('WEBAPP_COMING_TO_STREAMING_TOMORROW', { day: releaseDateFormatted.value });
	if (isTBA.value) return t('WEBAPP_COMING_TO_STREAMING_TBA');

	return t('WEBAPP_COMING_TO_STREAMING_ON_DATE', { date: releaseDateFormatted.value });
});
</script>

<style lang="scss" scoped>
.upcoming-banner {
	border-top-left-radius: 8px;
	border-top-right-radius: 8px;
	background-color: rgba(0, 41, 11, 0.75);
	color: #00e03c;
	font-size: 14px;
	font-weight: 400;
	font-style: normal;
	letter-spacing: 0.21px;
	padding: 8px 16px;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 8px;
}
</style>
